import { formatCoding, getQuestionnaireAnswers } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import EncounterServiceDateDiffFields from "@/encounters/components/EncounterServiceDateDiffFields/EncounterServiceDateDiffFields";
import {
  REASON_FOR_TRANSFER_LABEL,
  TransferOfCareEncounterLinkId,
} from "@/encounters/constants/transfer-of-care";
import { resolveEncounterInfoFields } from "@/encounters/utils/encounter-detail-version-diff";
import {
  getEncounterServiceDateDiffContext,
  getEncounterServiceDateDiffContextFromEncounters,
} from "@/encounters/utils/encounter-service-date-diff";

const TransferOfCareEncounterDiff = ({
  currentVersion,
  previousVersion,
  currentEncounter,
  previousEncounter,
}: {
  currentVersion?: QuestionnaireResponse;
  previousVersion?: QuestionnaireResponse;
  currentEncounter?: Encounter;
  previousEncounter?: Encounter;
}) => {
  const hasEncounterPair = Boolean(currentEncounter && previousEncounter);
  if (!hasEncounterPair && (!currentVersion || !previousVersion)) return null;

  const currentAnswers = currentVersion ? getQuestionnaireAnswers(currentVersion) : {};
  const previousAnswers = previousVersion ? getQuestionnaireAnswers(previousVersion) : {};

  const {
    currentTemplate,
    previousTemplate,
    currentType,
    previousType,
    currentLocation,
    previousLocation,
    serviceDateDiff,
  } = resolveEncounterInfoFields(
    currentEncounter,
    previousEncounter,
    () => ({
      currentTemplate: formatCoding(
        currentAnswers[TransferOfCareEncounterLinkId.EncounterTemplate]?.valueCoding,
      ),
      previousTemplate: formatCoding(
        previousAnswers[TransferOfCareEncounterLinkId.EncounterTemplate]?.valueCoding,
      ),
      currentType: formatCoding(
        currentAnswers[TransferOfCareEncounterLinkId.ClinicalJustification]?.valueCoding,
      ),
      previousType: formatCoding(
        previousAnswers[TransferOfCareEncounterLinkId.ClinicalJustification]?.valueCoding,
      ),
      currentLocation: formatCoding(
        currentAnswers[TransferOfCareEncounterLinkId.EncounterLocation]?.valueCoding,
      ),
      previousLocation: formatCoding(
        previousAnswers[TransferOfCareEncounterLinkId.EncounterLocation]?.valueCoding,
      ),
      serviceDateDiff: getEncounterServiceDateDiffContext({
        currentAnswers,
        previousAnswers,
        answersFormat: "single",
      }),
    }),
    (current, previous) => ({
      currentTemplate: formatCoding(current.serviceType?.coding?.[0]),
      previousTemplate: formatCoding(previous.serviceType?.coding?.[0]),
      currentType: formatCoding(current.reasonCode?.[0]?.coding?.[0]),
      previousType: formatCoding(previous.reasonCode?.[0]?.coding?.[0]),
      currentLocation: formatCoding(current.class),
      previousLocation: formatCoding(previous.class),
      serviceDateDiff: getEncounterServiceDateDiffContextFromEncounters(current, previous),
    }),
  );

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Encounter Information</Title>
      </Stack>
      <Group grow align="flex-start">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Template</TextWrapper>
              <WordsDiff showRemoved current={currentTemplate} previous={previousTemplate} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>{REASON_FOR_TRANSFER_LABEL}</TextWrapper>
              <WordsDiff showRemoved current={currentType} previous={previousType} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Location</TextWrapper>
              <WordsDiff showRemoved current={currentLocation} previous={previousLocation} />
            </Stack>
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="previous" diff={serviceDateDiff} />
            )}
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Template</TextWrapper>
              <WordsDiff current={currentTemplate} previous={previousTemplate} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>{REASON_FOR_TRANSFER_LABEL}</TextWrapper>
              <WordsDiff current={currentType} previous={previousType} />
            </Stack>

            <Stack gap="0">
              <TextWrapper fw={600}>Location</TextWrapper>
              <WordsDiff current={currentLocation} previous={previousLocation} />
            </Stack>
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="amended" diff={serviceDateDiff} />
            )}
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default TransferOfCareEncounterDiff;
